import { DirectSalesAsset, DirectSalesMediaType, DirectSalesStatusOption } from './DirectSalesCampaign.types';
import { MetricType } from './DrilldownReports.types';

export interface TouchedPoint {
  latitude: number;
  longitude: number;
  radius: number;
}

export interface CampaignPlanPoi {
  categoryName: string | null;
  categoryType: string;
  displayName: string;
  poiId: string;
  latitude: number | null;
  longitude: number | null;
  radius: number;
}

export interface CampaignPlanFilterOptions {
  mediaType: DirectSalesMediaType[];
  channel: string[];
  format: string[];
  range: { startDate: string; endDate: string };
  pois: CampaignPlanPoi[];
}

export interface CampaignPlanAllocationReportDealLineSelectConfig {
  isDefault?: boolean;
  isLocked?: boolean;
  isHidden?: boolean;
}

export interface CampaignPlanAllocationReportConfig {
  sot: {
    planned: boolean;
  };
  impressions: {
    planned: boolean;
  };
  indexedPrice: {
    planned: boolean;
  };
  grossPrice: {
    planned: boolean;
  };
  allowedGroupByOptions: string[];
  dealLineSelect?: CampaignPlanAllocationReportDealLineSelectConfig;
}

export type CampaignPlanAllocationMetricId = Exclude<
  keyof CampaignPlanAllocationReportConfig,
  'allowedGroupByOptions' | 'dealLineSelect'
>;

export type CampaignPlanFilterRequest = Partial<Omit<CampaignPlanFilterOptions, 'pois'> & { poi: string[] }>;

export interface CampaignPlanDetails {
  campaignId: string;
  campaignName: string;
  advertiserName: string;
  brandName: string;
  campaignStatus: DirectSalesStatusOption;
  startDate: string;
  endDate: string;
  lastUpdatedAt: string;
  dealLines: {
    dealLineId: string;
    dealLineState: DirectSalesStatusOption;
    dealLineName: string;
  }[];
  totalCampaignImpressions: number;
  totalCampaignFrames: number;
  metaData: {
    isoCountryCode: string;
    timeZone: string;
    latitude: number | null;
    longitude: number | null;
    includeMap?: boolean;
    includePOIs?: boolean;
    allocationReport?: CampaignPlanAllocationReportConfig;
    metricType?: MetricType;
  };
  assets: {
    filteredAssets: DirectSalesAsset[];
    missingAssets: DirectSalesAsset[];
  };
  filterOptions?: CampaignPlanFilterOptions;
  touchedPoints?: TouchedPoint[];
}

export const CampaignPlanErrorTitle = {
  UNAUTHORIZED: 'Unauthorized',
};

export interface CampaignPlanError {
  detail?: string;
  title?: string;
}

export class CampaignPlanErrorEvent extends Error implements CampaignPlanError {
  public detail?: string;

  public title?: string;

  constructor(params?: { message?: string; detail?: string; title?: string }) {
    super(params?.message);
    this.detail = params?.message || params?.detail;
    this.title = params?.title;
  }
}

export type CampaignPlanResponse = CampaignPlanDetails | CampaignPlanError;

export interface CampaignPlanShareConfig {
  token?: string;
  expiresAt?: string;
}

export interface CampaignPlanConfig {
  allocationReport: CampaignPlanAllocationReportConfig;
  map: {
    includeMap: boolean;
    includePOIs: boolean;
  };
  share?: CampaignPlanShareConfig;
}

export type ActiveColorFilterType = 'channel' | 'format' | 'mediaType';

export type CampaignPlanConfigResponse = Partial<CampaignPlanConfig>;

export interface ActiveColorFilter {
  type: ActiveColorFilterType;
  colorMap: Map<string, number[]>;
}
